import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import { libraryAPI } from '../../services/library.service';
import { formatDate } from '../../utils/dateUtils';
import { ensureHttps } from '../../utils/urlUtils';
import { 
  FaDownload, 
  FaBookReader, 
  FaStar, 
  FaEye, 
  FaCalendar, 
  FaRulerVertical, 
  FaBarcode, 
  FaBuilding, 
  FaLanguage, 
  FaFileAlt 
} from 'react-icons/fa';
import './BookDetailPage.scss';

const BookDetailPage = () => {
  const { slug } = useParams();
  const { t, getLocalizedField, language } = useSettings();
  const { user, isAuthenticated } = useAuth();
  
  const [book, setBook] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Review form state
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [showReader, setShowReader] = useState(false);

  useEffect(() => {
    fetchBookDetails();
  }, [slug]);

  const fetchBookDetails = async () => {
    try {
      setLoading(true);
      const [bookRes, reviewsRes] = await Promise.all([
        libraryAPI.getBookBySlug(slug),
        libraryAPI.getReviews(slug)
      ]);
      setBook(bookRes.data);
      setReviews(reviewsRes.data);
    } catch (err) {
      console.error('Error fetching book details:', err);
      // More specific error handling could go here
      setError('Failed to load book details');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      // Just triggering the download endpoint to count it, standard file access via browser
      await libraryAPI.downloadBook(slug);
    } catch (err) {
      console.error('Download count error:', err);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) return;
    
    setSubmitting(true);
    setReviewError(null);
    
    try {
      await libraryAPI.createReview({
        book: book.id,
        rating,
        text: comment
      });
      
      // Refresh reviews
      const reviewsRes = await libraryAPI.getReviews(slug);
      setReviews(reviewsRes.data);
      setComment('');
      setRating(5);
    } catch (err) {
      setReviewError(err.response?.data?.detail || err.response?.data?.[0] || 'Error posting review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading-container"><div className="loader"></div></div>;
  if (error || !book) return <div className="error-container">{error || 'Book not found'}</div>;

  return (
    <div className="book-detail-page">
      <div className="container">
        
        {/* Book Hero Section */}
        <div className="book-hero">
          <div className="book-cover-wrapper">
             {book.cover ? (
               <img src={ensureHttps(book.cover)} alt={getLocalizedField(book, 'title')} className="book-cover" />
             ) : (
               <div className="book-cover-placeholder">
                 <FaFileAlt size={64} />
               </div>
             )}
          </div>
          
          <div className="book-main-info">
            <h1 className="book-title">{getLocalizedField(book, 'title')}</h1>
            
            <div className="book-authors">
              {book.authors && book.authors.map(author => (
                <span key={author.id} className="author-badge">
                  {author.full_name}
                </span>
              ))}
            </div>
            
            <div className="book-stats-row">
              <div className="stat-item">
                <FaStar className="icon star" />
                <span>{book.rating || '0.0'}</span>
              </div>
              <div className="stat-item">
                <FaEye className="icon" />
                <span>{book.views_count || 0} {t('bookViews')}</span>
              </div>
            </div>

            <div className="book-actions">
              {book.file && (
                <>
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setShowReader(true);
                      if (isAuthenticated) {
                        handleDownload();
                      }
                      setTimeout(() => {
                        document.getElementById('book-reader')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }} 
                  >
                    <FaBookReader className="icon" />
                    {t('bookRead')}
                  </button>
                  
                  <a 
                    href={ensureHttps(book.file)} 
                    download 
                    className="btn btn-secondary"
                    onClick={() => handleDownload()}
                  >
                    <FaDownload className="icon" />
                    {t('bookDownload')}
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Book Details Grid */}
        <div className="book-meta-grid">
          <div className="meta-card">
            <FaCalendar className="meta-icon" />
            <div className="meta-content">
              <span className="meta-label">{t('bookYear')}</span>
              <span className="meta-value">{book.publish_year || '-'}</span>
            </div>
          </div>
          
          <div className="meta-card">
            <FaRulerVertical className="meta-icon" />
            <div className="meta-content">
              <span className="meta-label">{t('bookPages')}</span>
              <span className="meta-value">{book.pages || '-'}</span>
            </div>
          </div>

          <div className="meta-card">
            <FaBuilding className="meta-icon" />
            <div className="meta-content">
              <span className="meta-label">{t('bookPublisher')}</span>
              <span className="meta-value">{book.publisher || '-'}</span>
            </div>
          </div>

          <div className="meta-card">
            <FaBarcode className="meta-icon" />
            <div className="meta-content">
              <span className="meta-label">{t('bookISBN')}</span>
              <span className="meta-value">{book.isbn || '-'}</span>
            </div>
          </div>

          <div className="meta-card">
            <FaLanguage className="meta-icon" />
            <div className="meta-content">
              <span className="meta-label">{t('bookLanguage')}</span>
              <span className="meta-value">{book.language === 'ky' ? 'Кыргызча' : book.language === 'ru' ? 'Русский' : book.language === 'en' ? 'English' : book.language}</span>
            </div>
          </div>

          <div className="meta-card">
            <FaFileAlt className="meta-icon" />
            <div className="meta-content">
              <span className="meta-label">{t('bookFormat')}</span>
              <span className="meta-value">
                {book.file_format || 'PDF'} 
                {book.file_size_mb ? ` (${book.file_size_mb} MB)` : ''}
              </span>
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="book-description-section">
          <h2>{t('bookDescription')}</h2>
          <div className="description-content">
            {getLocalizedField(book, 'description') || t('noDescription')}
          </div>
        </div>

        {/* Online Reader Section */}
        {showReader && book.file && (
          <div className="book-reader-section" id="book-reader">
            <div className="reader-header">
              <h2>{t('bookReadOnline') || 'Read Online'}</h2>
              <button 
                className="close-reader-btn"
                onClick={() => setShowReader(false)}
                aria-label="Close Reader"
              >
                ×
              </button>
            </div>
            <div className="reader-frame-container">
              <iframe 
                src={ensureHttps(book.file)} 
                title={getLocalizedField(book, 'title')}
                className="reader-frame"
              />
            </div>
          </div>
        )}

        {/* Reviews Section */}
        <div className="book-reviews-section">
          <h2>{t('bookReviews')} ({book.reviews_count || reviews.length})</h2>
          
          {/* Review Form */}
          {isAuthenticated ? (
            <form className="review-form" onSubmit={handleSubmitReview}>
              <h3>{t('bookAddReview')}</h3>
              <div className="rating-select">
                {[1, 2, 3, 4, 5].map(star => (
                  <FaStar 
                    key={star}
                    className={`star-select ${star <= rating ? 'active' : ''}`}
                    onClick={() => setRating(star)}
                  />
                ))}
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('bookYourReview')}
                required
              />
              {reviewError && <div className="error-msg">{reviewError}</div>}
              <button type="submit" disabled={submitting} className="btn-submit">
                {submitting ? '...' : t('bookSubmitReview')}
              </button>
            </form>
          ) : (
            <div className="login-prompt">
              <p>{t('bookLoginToReview')}</p>
            </div>
          )}

          {/* Reviews List */}
          <div className="reviews-list">
            {reviews.length > 0 ? (
              reviews.map(review => (
                <div key={review.id} className="review-card">
                  <div className="review-header">
                    <span className="reviewer-name">{review.user_name}</span>
                    <span className="review-date">
                      {formatDate(review.created_at, language)}
                    </span>
                  </div>
                  <div className="review-rating">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className={i < review.rating ? 'filled' : ''} />
                    ))}
                  </div>
                  <p className="review-text">{review.text}</p>
                </div>
              ))
            ) : (
              <p className="no-reviews">{t('bookNoReviews')}</p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default BookDetailPage;
